import {
  type BindInWhenOnFluentSyntax,
  type BindToFluentSyntax,
  type DynamicValueBuilder,
  Container as InversifyContainer,
  type ResolutionContext,
  type ServiceIdentifier,
} from 'inversify';

import type {Nullable} from '../utils/utility-types';

export class ContainerBinder {
  private identifiers = new Set<ContainerBinder.Identifier<unknown>>();

  constructor(public container: ContainerBinder.Container) {}

  static createContainer(parentContainer?: ContainerBinder.Container) {
    return new InversifyContainer({parent: parentContainer});
  }

  bind<T>(
    serviceIdentifier: ContainerBinder.Identifier<T>,
    bindTo: ContainerBinder.BindTo<T> = null,
    options: ContainerBinder.Options<T> = {},
  ) {
    return this.bindTo(serviceIdentifier, bindTo, false, options);
  }

  bindFactory<T>(
    key: ContainerBinder.Identifier<T>,
    factory: Parameters<BindToFluentSyntax<T>['toFactory']>[0],
  ) {
    this.container.bind(key).toFactory(factory);

    return this;
  }

  bindSingleton<T>(
    serviceIdentifier: ContainerBinder.Identifier<T>,
    bindTo: ContainerBinder.BindTo<T> = null,
    options: ContainerBinder.Options<T> = {},
  ) {
    return this.bindTo(serviceIdentifier, bindTo, true, options);
  }

  done() {
    return this.container;
  }

  toDynamicValue<T>(
    key: ContainerBinder.Identifier<T>,
    toDynamicBinder: ContainerBinder.DynamicValue<T>,
    isSingleton: boolean = false,
    options: ContainerBinder.Options<T> = {},
  ) {
    const binding = this.container.bind<T>(key).toDynamicValue(toDynamicBinder);

    this.processOptions(binding, options, isSingleton);

    return this;
  }

  private bindTo<T>(
    serviceIdentifier: ContainerBinder.Identifier<T>,
    bindTo: ContainerBinder.BindTo<T>,
    isSingleton: boolean,
    options: ContainerBinder.Options<T>,
  ) {
    const binding = this.createBinding<T>(serviceIdentifier, bindTo);

    this.processOptions(binding, options, isSingleton);

    return this;
  }

  private createBinding<T>(
    identifier: ContainerBinder.Identifier<T>,
    to: ContainerBinder.BindTo<T>,
  ) {
    let binding: ContainerBinder.Binding<T>;

    if (this.identifiers.has(identifier)) {
      throw new Error(
        // @ts-expect-error: TODO: describe why
        `Identifier ${String(identifier.name || identifier.toString())} already bound`,
      );
    }

    this.identifiers.add(identifier);

    if (!to) {
      binding = this.container.bind(identifier).toSelf();
    } else {
      binding = this.container.bind(identifier).to(to);
    }

    return binding;
  }

  private processOptions<T>(
    binding: ContainerBinder.Binding<T>,
    options: ContainerBinder.Options<T>,
    isSingleton: boolean,
  ) {
    if (isSingleton) {
      binding.inSingletonScope();
    }

    if (options.onActivation) {
      binding.onActivation(options.onActivation);
    }

    if (options.onDeactivation) {
      binding.onDeactivation(options.onDeactivation);
    }

    if (options.whenParentTagged) {
      binding.whenParentNamed(options.whenParentTagged);
    }
  }
}

export namespace ContainerBinder {
  export type Binding<T> = BindInWhenOnFluentSyntax<T>;

  export type BindTo<T> = Nullable<new (...args: never[]) => T>;

  export type Container = InversifyContainer;

  export type Context = ResolutionContext;

  export type DynamicValue<T> = DynamicValueBuilder<T>;

  export type Identifier<T> = ServiceIdentifier<T>;

  export type OnActivationCb<T> = (
    context: ResolutionContext,
    injectable: T,
  ) => Promise<T> | T;

  export type OnDeactivationCb<T> = (injectable: T) => Promise<void> | void;

  export type Options<T> = {
    onActivation?: OnActivationCb<T>;
    onDeactivation?: OnDeactivationCb<T>;
    whenParentTagged?: string;
  };
}
